import { useEffect, useRef, useState } from "react";
import { planwireApi, ApiError } from "@/api";
import type { JobApplication, PlanWireStatusResponse } from "@/types";
import { Button, Pill, Spinner } from "@/components/ui";
import { formatDateShort, formatRelative } from "@/lib/format";
import "./planwire.css";

/** Planning statuses that mean the application is decided — PlanWire stops being
 *  relevant once any of these is reached (the outcome is already known). */
const PLANWIRE_DECIDED_STATUSES = new Set([
  "approved",
  "approved_with_conditions",
  "refused",
  "withdrawn",
  "appeal_approved",
  "appeal_dismissed",
]);

/** PlanWire (live planning-status lookup) is planning-only, and only useful while
 *  the application is in-flight — i.e. open and awaiting a decision. Once it's
 *  closed or decided (approved/refused/withdrawn) the outcome is known. It also
 *  needs an `app_reference` — that's the search key, so there's nothing to look up
 *  without one. Finally, once the application already carries an
 *  `application_view_url` (its council-portal link is on record) the lookup adds
 *  nothing, so we hide it — PlanWire's main job is to surface that link. Shared by
 *  the job-detail card and the cross-job Open-applications card so both gate the
 *  panel identically. */
export function isPlanWireRelevant(app: {
  type: string | null;
  status: string | null;
  is_open: boolean;
  app_reference: string | null;
  application_view_url?: string | null;
}): boolean {
  return (
    app.type === "planning" &&
    app.is_open &&
    (app.app_reference ?? "").trim() !== "" &&
    (app.application_view_url ?? "").trim() === "" &&
    !(app.status != null && PLANWIRE_DECIDED_STATUSES.has(app.status))
  );
}

/** Planning status (PlanWire) — a READ-ONLY, on-demand pull of the live planning
 *  status PlanWire holds for this application. Collapsed to a button by default;
 *  the lookup is a billed third-party call, so it fires only on the explicit
 *  "Check planning status" click (and re-runs on "Refresh"), caching the last
 *  result in component state for the session.
 *
 *  This panel is informational ONLY. PlanWire's `status`/`decision` are raw
 *  scraped portal strings (per-council vocab) that do NOT map to the internal
 *  `application.status` enum — they're rendered verbatim. The user reads them and,
 *  if warranted, edits the internal status through the existing control. There is
 *  deliberately no write-back here.
 *
 *  Needs only the application `id` for the lookup, so it accepts any row carrying
 *  one (job-detail's full {@link JobApplication} or the Open-apps projection). */
export function PlanWirePanel({ app }: { app: Pick<JobApplication, "id"> }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<PlanWireStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Abort any in-flight lookup if the card unmounts.
  useEffect(() => () => abortRef.current?.abort(), []);

  async function check() {
    if (loading) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);
    try {
      const res = await planwireApi.getApplicationStatus(app.id, ctrl.signal);
      setResult(res);
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return; // superseded/unmounted
      if (err instanceof ApiError && err.isAuthError) {
        setError("You don't have permission to check the planning status.");
      } else {
        // 500 (ERROR_FATAL — PlanWire down/quota) or network/timeout: keep the
        // user-facing copy generic, log the detail for debugging.
        if (err instanceof ApiError) console.error("PlanWire lookup failed:", err.message);
        setError("Couldn't reach PlanWire — please try again later.");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="planwire" aria-label="Planning status (PlanWire)">
      <div className="planwire__head">
        <span className="planwire__title">Planning status (PlanWire)</span>
        <Button
          variant={result || error ? "ghost" : "secondary"}
          size="sm"
          loading={loading}
          onClick={check}
        >
          {result || error ? "Refresh" : "Check planning status"}
        </Button>
      </div>

      {loading && !result ? (
        <div className="planwire__muted planwire__loading">
          <Spinner label="Checking PlanWire…" />
          <span>Checking PlanWire…</span>
        </div>
      ) : null}

      {error ? (
        <p className="planwire__error" role="alert">{error}</p>
      ) : null}

      {result && !error ? <PlanWireResult result={result} /> : null}
    </section>
  );
}

function PlanWireResult({ result }: { result: PlanWireStatusResponse }) {
  // Branch on `covered` first, then `found`. The two neutral cases aren't errors.
  if (!result.covered) {
    return (
      <p className="planwire__muted">
        PlanWire doesn't cover {result.authority_name || "this authority"} yet.
      </p>
    );
  }
  if (!result.found) {
    return (
      <p className="planwire__muted">
        No matching record found in PlanWire for{" "}
        <span className="numeric">{result.app_reference || "this reference"}</span>.
      </p>
    );
  }

  const { decision, status, dates, application_type, view_url, raw } = result;
  const dataStatus = rawString(raw, ["freshness", "dataStatus"]);
  const lastScrapedAt = rawString(raw, ["source", "lastScrapedAt"]);
  const isStale = dataStatus === "stale";

  return (
    <div className="planwire__body">
      <div className="planwire__decision">
        <span className="planwire__decision-text">{decision || "No decision recorded yet"}</span>
        {status ? <Pill tone="info">{status}</Pill> : null}
      </div>

      <dl className="planwire__facts">
        {dates.decisionDate ? (
          <Fact label="Decision date" value={formatDateShort(dates.decisionDate)} />
        ) : null}
        {application_type ? <Fact label="Application type" value={application_type} /> : null}
        {dates.receivedDate ? (
          <Fact label="Received" value={formatDateShort(dates.receivedDate)} />
        ) : null}
        {dates.validatedDate ? (
          <Fact label="Validated" value={formatDateShort(dates.validatedDate)} />
        ) : null}
      </dl>

      {isStale ? (
        <p className="planwire__stale" role="status">
          PlanWire data may be behind the council portal
          {lastScrapedAt ? ` — last checked ${formatRelative(lastScrapedAt)}` : ""}. Verify on the
          portal before acting.
        </p>
      ) : null}

      <div className="planwire__footrow">
        {lastScrapedAt ? (
          <span className="planwire__freshness">Last checked: {formatRelative(lastScrapedAt)}</span>
        ) : null}
        {view_url ? (
          <a className="planwire__link" href={view_url} target="_blank" rel="noopener noreferrer">
            View on council portal ↗
          </a>
        ) : null}
      </div>

      <details className="planwire__raw">
        <summary>Raw PlanWire data</summary>
        <pre>{JSON.stringify(raw, null, 2)}</pre>
      </details>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

/** Safely read a nested string out of the best-effort `raw` record (any field may
 *  be absent or the wrong type). Returns null unless the full path lands on a string. */
function rawString(raw: Record<string, unknown>, path: string[]): string | null {
  let cur: unknown = raw;
  for (const key of path) {
    if (cur && typeof cur === "object" && key in cur) {
      cur = (cur as Record<string, unknown>)[key];
    } else {
      return null;
    }
  }
  return typeof cur === "string" && cur ? cur : null;
}
